import React from "react";
import { useFormikContext } from "formik";
import { Row, Col, Form } from "react-bootstrap";
import { Autocomplete, Box } from "@mui/material";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import { MecordColors } from "Constants/Color";
import { autocompleteDropdownStyles } from "Constants/Style";
import { parse, format } from "date-fns";

const applicableOptions = ["Applicable", "Not Applicable"];

const InspectionInstallationSection: React.FC = () => {
  const { values, setFieldValue, errors, touched } = useFormikContext<any>();

  const inspectionApplicable = values.inspection === "Applicable";
  const installationApplicable = values.installation === "Applicable";

  // Convert date to YYYY-MM-DD for input (HTML date input format)
  // Handles UTC ISO strings, YYYY-MM-DD, and dd-MMM-yyyy formats
  const convertToInputFormat = (dateStr: string): string => {
    if (!dateStr) return "";
    try {
      // If already in YYYY-MM-DD format, return as-is
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        return dateStr;
      }
      
      // If it's a UTC ISO string (contains 'T' or 'Z'), convert to local YYYY-MM-DD
      if (dateStr.includes("T") || dateStr.includes("Z")) {
        const utcDate = new Date(dateStr);
        if (isNaN(utcDate.getTime())) return "";
        // Get local date components
        const year = utcDate.getFullYear();
        const month = String(utcDate.getMonth() + 1).padStart(2, "0");
        const day = String(utcDate.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      }
      
      // Try parsing dd-MMM-yyyy format (for backward compatibility)
      const parsed = parse(dateStr, "dd-MMM-yyyy", new Date());
      if (!isNaN(parsed.getTime())) {
        return format(parsed, "yyyy-MM-dd");
      }
      
      return "";
    } catch {
      return "";
    }
  };

  // Convert date from YYYY-MM-DD to YYYY-MM-DD for storage
  // (We keep it in YYYY-MM-DD format, which will be converted to UTC in the thunk)
  const convertToStorageFormat = (dateStr: string): string => {
    if (!dateStr) return "";
    // Just return the YYYY-MM-DD format as-is
    // The thunk will convert it to UTC before sending to backend
    return dateStr;
  };

  return (
    <div className="rounded mb-3">
      <div className="d-flex justify-content-between align-items-center mb-3 gap-2">
        <h5 className="fw-semibold">Inspection & Installation Details</h5>
      </div>
      <Row
        className="g-3 pb-3 px-1 rounded"
        // style={{ backgroundColor: MecordColors?.LightGrayColor }}
      >
        <Col xs={12} sm={6} md={3}>
          <Form.Group>
            <Form.Label>Inspection</Form.Label>
            <Autocomplete
              disableClearable
              options={applicableOptions}
              getOptionLabel={(option: any) => option}
              isOptionEqualToValue={(option: any, value: any) =>
                option === value
              }
              value={values.inspection || null}
              onChange={(_: any, newValue: any) => {
                setFieldValue("inspection", newValue || "");
                if (newValue !== "Applicable") {
                  setFieldValue("inspection_date", "");
                }
              }}
              ListboxProps={autocompleteDropdownStyles.ListboxProps}
              renderOption={(props, option, { selected }) => (
                <li {...props} style={{ padding: 0 }}>
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.8,
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: MecordColors.Hover,
                        color: MecordColors.ActiveText,
                      },
                      ...(selected && {
                        backgroundColor: MecordColors.ActiveBackground,
                        color: MecordColors.ActiveText,
                      }),
                    }}
                  >
                    <span>{option}</span>
                  </Box>
                </li>
              )}
              renderInput={(params: any) => (
                <BootstrapTextField
                  {...params}
                  placeholder="Select"
                  size="small"
                  sx={autocompleteDropdownStyles.inputSx}
                />
              )}
            />
          </Form.Group>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Form.Group>
            <Form.Label>Inspection Date</Form.Label>
            <Form.Control
              type="date"
              name="inspection_date"
              value={convertToInputFormat(values.inspection_date || "")}
              onChange={(e) => {
                const converted = convertToStorageFormat(e.target.value);
                setFieldValue("inspection_date", converted);
              }}
              disabled={!inspectionApplicable}
              isInvalid={!!(errors.inspection_date && touched.inspection_date)}
            />
            {errors.inspection_date && touched.inspection_date && (
              <Form.Control.Feedback type="invalid">
                {errors.inspection_date as string}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Form.Group>
            <Form.Label>Installation</Form.Label>
            <Autocomplete
              disableClearable
              options={applicableOptions}
              getOptionLabel={(option: any) => option}
              isOptionEqualToValue={(option: any, value: any) =>
                option === value
              }
              value={values.installation || null}
              onChange={(_: any, newValue: any) => {
                setFieldValue("installation", newValue || "");
                if (newValue !== "Applicable") {
                  setFieldValue("installation_date", "");
                }
              }}
              ListboxProps={autocompleteDropdownStyles.ListboxProps}
              renderOption={(props, option, { selected }) => (
                <li {...props} style={{ padding: 0 }}>
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.8,
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: MecordColors.Hover,
                        color: MecordColors.ActiveText,
                      },
                      ...(selected && {
                        backgroundColor: MecordColors.ActiveBackground,
                        color: MecordColors.ActiveText,
                      }),
                    }}
                  >
                    <span>{option}</span>
                  </Box>
                </li>
              )}
              renderInput={(params: any) => (
                <BootstrapTextField
                  {...params}
                  placeholder="Select"
                  size="small"
                  sx={autocompleteDropdownStyles.inputSx}
                />
              )}
            />
          </Form.Group>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Form.Group>
            <Form.Label>Installation Date</Form.Label>
            <Form.Control
              type="date"
              name="installation_date"
              value={convertToInputFormat(values.installation_date || "")}
              onChange={(e) => {
                const converted = convertToStorageFormat(e.target.value);
                setFieldValue("installation_date", converted);
              }}
              disabled={!installationApplicable}
              isInvalid={
                !!(errors.installation_date && touched.installation_date)
              }
            />
            {errors.installation_date && touched.installation_date && (
              <Form.Control.Feedback type="invalid">
                {errors.installation_date as string}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Col>
      </Row>
    </div>
  );
};

export default InspectionInstallationSection;
